import {MutableRefObject, RefObject, useCallback, useLayoutEffect, useRef, useState} from 'react';

import {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {
  diffTableGeometry,
  HEADER_COLUMN_SELECTOR,
  isSameSize,
  MAIN_TABLE_SELECTOR,
  readTableGeometry,
  TABLE_NAME_SELECTOR,
  TABLE_WRAPPER_SELECTOR,
  TableGeometry,
} from 'notebook/tables/core-ui/TableGeometry';
import findScrollContainer from 'util/findScrollContainer';

/**
 * Cadence (ms) of the fallback re-measurement that bounds how long a missed resize trigger can leave
 * table geometry stale.
 */
export const FALLBACK_MEASUREMENT_INTERVAL_MS = 3000;
/**
 * Cadence (ms) of the vertical scroll container lookup while it resolves to the body, for pages whose
 * scroll CSS is applied after the table mounts.
 */
export const SCROLL_CONTAINER_RETRY_MS = 500;
/**
 * Delay (ms) between the virtualized table rendering different rows and measuring them. Bounds scroll
 * cost to a few measurements a second and lets the new rows commit before they are read.
 */
export const ROWS_RENDERED_MEASUREMENT_DELAY_MS = 100;
/** Measurements the fallback finds stale are reported in development builds only; behavior is identical. */
const REPORT_MISSED_TRIGGERS = process.env.NODE_ENV === 'development';

/**
 * Whether the measured body rows differ in height. Reordering such rows changes `rowHeights` without
 * resizing the table, so no observed element reports it. The first measured row carries a border
 * adjustment and is excluded.
 */
export function hasVaryingRowHeights(rowHeights: Array<number>): boolean {
  const bodyRowHeights = rowHeights.slice(1);
  return bodyRowHeights.some((height) => !isSameSize(height, bodyRowHeights[0]));
}

const MAX_DESCRIBED_ROW_HEIGHT_CHANGES = 5;

/**
 * Describes which measured elements are currently observed and when the observer last fired, for the
 * missed-trigger diagnostic.
 */
function describeObservation(
  root: Element,
  observed: Set<Element>,
  lastNotificationAt: number | null
): string {
  const elements: Array<[string, Element | null]> = [
    ['root', root],
    ['mainTable', root.querySelector(MAIN_TABLE_SELECTOR)],
    ['wrapper', root.querySelector(TABLE_WRAPPER_SELECTOR)],
    ['headerColumn', root.querySelector(HEADER_COLUMN_SELECTOR)],
    ['tableName', root.querySelector(TABLE_NAME_SELECTOR)],
  ];
  const status = elements
    .map(
      ([name, element]) =>
        `${name}=${element == null ? 'absent' : observed.has(element) ? 'observed' : 'NOT observed'}`
    )
    .join(' ');
  const lastFired =
    lastNotificationAt == null ? 'never' : `${Math.round(Date.now() - lastNotificationAt)}ms ago`;
  return `Observer: ${observed.size} elements, last fired ${lastFired}; ${status}.`;
}

/**
 * Describes how measured row heights changed, for the missed-trigger diagnostic: which entries changed,
 * whether the entry count changed, and whether the total stayed the same (a change no resize of the main
 * `<table>` would report). Entry 0 is the column header row.
 */
export function describeRowHeightChanges(previous: Array<number>, next: Array<number>): string {
  const changed: Array<string> = [];
  for (let i = 0; i < Math.max(previous.length, next.length); i++) {
    if (previous[i] !== next[i]) {
      changed.push(`[${i}] ${previous[i] ?? '-'} -> ${next[i] ?? '-'}`);
    }
  }
  const total = (heights: Array<number>): number => Math.round(heights.reduce((acc, h) => acc + h, 0));
  const shown = changed.slice(0, MAX_DESCRIBED_ROW_HEIGHT_CHANGES).join(', ');
  const more =
    changed.length > MAX_DESCRIBED_ROW_HEIGHT_CHANGES
      ? ` and ${changed.length - MAX_DESCRIBED_ROW_HEIGHT_CHANGES} more`
      : '';
  return (
    `Row heights: ${previous.length} -> ${next.length} entries, total ${total(previous)}px -> ` +
    `${total(next)}px; changed ${shown}${more}.`
  );
}

interface UseTableMeasurementArgs {
  rootRef: RefObject<HTMLElement>;
  state: TableEditableState;
  setState: (state: Partial<TableEditableState>) => void;
  isCollapsed: boolean;
  /** The table's row data. A new value re-measures tables whose rows differ in height. */
  rows: unknown;
  showCellEditor: boolean;
}

interface TableMeasurementHandle {
  /** The page's vertical scroll container, for imperative reads. */
  verticalScrollContainerRef: MutableRefObject<Element | null>;
  /**
   * Whether the body is standing in for the scroll container until the real one is found. Rendered so
   * that finding it re-renders the components that take the container as a prop (Virtuoso's scroll
   * parent, the sticky header).
   */
  verticalScrollContainerIsBody: boolean;
  /** Call with the rendered row indices whenever the virtualized table reports its rendered rows. */
  onRowsRendered: (renderedRowIndices: Array<number>) => void;
}

/**
 * Keeps the table's DOM-measured geometry on `state` current.
 *
 * Measurement runs when a `ResizeObserver` reports that a measured element changed size, shortly after
 * the virtualized table renders a different set of rows, when the table expands, and when row data
 * changes on a table whose rows differ in height. A fallback interval bounds how long anything those triggers miss can stay
 * stale; in development builds it also logs every measurement the triggers missed.
 *
 * Also finds the page's vertical scroll container, looking again every {@link SCROLL_CONTAINER_RETRY_MS}
 * while it resolves to the body for pages that apply their scroll CSS after the table mounts, and marks `hasScrolledSinceEdit` when it scrolls during an edit.
 */
export default function useTableMeasurement({
  rootRef,
  state,
  setState,
  isCollapsed,
  rows,
  showCellEditor,
}: UseTableMeasurementArgs): TableMeasurementHandle {
  const latest = useRef({state, setState, isCollapsed, showCellEditor});
  latest.current = {state, setState, isCollapsed, showCellEditor};

  const verticalScrollContainerRef = useRef<Element | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const observedElementsRef = useRef<Set<Element>>(new Set());
  const rowsRenderedMeasurementRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rowsReportedWhilePendingRef = useRef(false);
  const lastRowsRenderedAtRef = useRef<number | null>(null);
  const lastObserverNotificationAtRef = useRef<number | null>(null);
  const lastMeasurementRef = useRef<{at: number; geometry: TableGeometry} | null>(null);
  const renderedRowsKeyRef = useRef<string | null>(null);
  const [verticalScrollContainerIsBody, setVerticalScrollContainerIsBody] = useState(false);

  const measure = useCallback(
    ({reportMissedTrigger = false}: {reportMissedTrigger?: boolean} = {}): void => {
      const root = rootRef.current;
      if (root == null || latest.current.isCollapsed) {
        return;
      }
      const geometry = readTableGeometry(root, verticalScrollContainerRef.current);
      const previousMeasurement = lastMeasurementRef.current;
      lastMeasurementRef.current = {at: Date.now(), geometry};
      const update = diffTableGeometry(latest.current.state, geometry);
      const staleFields = Object.keys(update) as Array<keyof typeof update>;
      if (staleFields.length === 0) {
        return;
      }
      if (reportMissedTrigger) {
        const lastRowsRenderedAt = lastRowsRenderedAtRef.current;
        const rowHeightChanges =
          update.rowHeights == null
            ? ''
            : ` ${describeRowHeightChanges(latest.current.state.rowHeights, update.rowHeights)}`;
        const fieldHistory = staleFields
          .filter((field) => field !== 'rowHeights')
          .map(
            (field) =>
              `${field}: state ${latest.current.state[field]}, now ${geometry[field]}, last measurement read ${
                previousMeasurement == null
                  ? 'nothing'
                  : `${previousMeasurement.geometry[field]} ${Math.round(Date.now() - previousMeasurement.at)}ms ago`
              }`
          )
          .join('; ');
        const message =
          `TableEditable: table geometry was stale (${staleFields.join(', ')}). ` +
          `A layout change was not caught by the size observer or a re-measure trigger. ${
            lastRowsRenderedAt == null
              ? 'The table has not reported rendered rows.'
              : `Rendered rows were last reported ${Math.round(Date.now() - lastRowsRenderedAt)}ms ago.`
          }${rowHeightChanges}${fieldHistory === '' ? '' : ` ${fieldHistory}.`} ${describeObservation(
            root,
            observedElementsRef.current,
            lastObserverNotificationAtRef.current
          )}`;
        const checkedAt = Date.now();
        // Reading geometry forces layout, so this can see a resize before the browser's next frame
        // delivers its observer notification. Only report the change if that frame passes without one.
        requestAnimationFrame(() => {
          setTimeout(() => {
            const notifiedAt = lastObserverNotificationAtRef.current;
            if (notifiedAt == null || notifiedAt < checkedAt) {
              // oxlint-disable-next-line no-console -- development-only diagnostic for a missing re-measure trigger
              console.error(message);
            }
          }, 0);
        });
      }
      latest.current.setState(update);
    },
    [rootRef]
  );

  /**
   * Keeps the observer attached to the elements whose geometry `readTableGeometry` reads. Rows are not
   * observed one by one: the main `<table>` stands in for them, because a rendered row changing height
   * changes the table's height. Row changes that keep the total height (reordering, a new set of
   * virtualized rows) are covered by the row-data and rows-rendered triggers instead. The body is never
   * observed; it is only the scroll container until the real one is found, and its height changes with
   * the whole document.
   */
  const syncObservedElements = useCallback((): void => {
    const observer = observerRef.current;
    const root = rootRef.current;
    if (observer == null || root == null) {
      return;
    }
    const elements = new Set(
      [
        root,
        root.querySelector(MAIN_TABLE_SELECTOR),
        root.querySelector(TABLE_WRAPPER_SELECTOR),
        root.querySelector(HEADER_COLUMN_SELECTOR),
        root.querySelector(TABLE_NAME_SELECTOR),
        verticalScrollContainerRef.current,
      ].filter((element): element is Element => element != null && element !== document.body)
    );
    observedElementsRef.current.forEach((element) => {
      if (!elements.has(element)) {
        observer.unobserve(element);
      }
    });
    elements.forEach((element) => {
      if (!observedElementsRef.current.has(element)) {
        observer.observe(element);
      }
    });
    observedElementsRef.current = elements;
  }, [rootRef]);

  // Layout effects so the scroll container and observer exist before the table's first paint.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (root == null) {
      return undefined;
    }
    const onScroll = (): void => {
      const {showCellEditor: isEditing, state: currentState, setState: write} = latest.current;
      if (isEditing && !currentState.hasScrolledSinceEdit) {
        write({hasScrolledSinceEdit: true});
      }
    };
    const findContainer = (): void => {
      verticalScrollContainerRef.current?.removeEventListener('scroll', onScroll);
      verticalScrollContainerRef.current = findScrollContainer(root);
      verticalScrollContainerRef.current?.addEventListener('scroll', onScroll);
    };
    findContainer();
    setVerticalScrollContainerIsBody(verticalScrollContainerRef.current === document.body);

    // The body should never be the scroll container of a table in Benchling, so finding it means the
    // page's scroll CSS was not applied yet. Keep looking until the real container appears.
    let retryId: ReturnType<typeof setInterval> | undefined;
    if (verticalScrollContainerRef.current === document.body) {
      retryId = setInterval(() => {
        findContainer();
        if (verticalScrollContainerRef.current !== document.body) {
          clearInterval(retryId);
          retryId = undefined;
          setVerticalScrollContainerIsBody(false);
          syncObservedElements();
          measure();
        }
      }, SCROLL_CONTAINER_RETRY_MS);
    }

    // Observing an element reports its initial size, which takes the first measurement.
    if (typeof ResizeObserver === 'undefined') {
      measure();
    } else {
      observerRef.current = new ResizeObserver(() => {
        lastObserverNotificationAtRef.current = Date.now();
        measure();
      });
      syncObservedElements();
    }

    const fallbackId = setInterval(() => {
      // A pending rows-rendered measurement is about to measure the rows that just rendered, so the
      // fallback leaves them to it rather than reporting them as missed.
      if (document.visibilityState !== 'hidden' && rowsRenderedMeasurementRef.current == null) {
        measure({reportMissedTrigger: REPORT_MISSED_TRIGGERS});
      }
    }, FALLBACK_MEASUREMENT_INTERVAL_MS);

    return () => {
      clearInterval(fallbackId);
      clearInterval(retryId);
      clearTimeout(rowsRenderedMeasurementRef.current);
      rowsRenderedMeasurementRef.current = undefined;
      rowsReportedWhilePendingRef.current = false;
      renderedRowsKeyRef.current = null;
      lastRowsRenderedAtRef.current = null;
      lastObserverNotificationAtRef.current = null;
      lastMeasurementRef.current = null;
      observerRef.current?.disconnect();
      observerRef.current = null;
      observedElementsRef.current = new Set();
      verticalScrollContainerRef.current?.removeEventListener('scroll', onScroll);
      verticalScrollContainerRef.current = null;
    };
  }, [rootRef, measure, syncObservedElements]);

  // Layout effects so these see the committed DOM and measure before it paints, which keeps affixed rows
  // from showing a frame at stale heights.
  // Measured elements mount and unmount as the table renders (collapse, single input view, row changes).
  useLayoutEffect(() => {
    syncObservedElements();
  });

  useLayoutEffect(() => {
    if (!isCollapsed) {
      measure();
    }
  }, [isCollapsed, measure]);

  useLayoutEffect(() => {
    if (hasVaryingRowHeights(latest.current.state.rowHeights)) {
      measure();
    }
  }, [rows, measure]);

  const scheduleRowsRenderedMeasurement = useCallback((): void => {
    function schedule(): void {
      rowsRenderedMeasurementRef.current = setTimeout(() => {
        rowsRenderedMeasurementRef.current = undefined;
        measure();
        // Rows reported while this measurement was pending may not have committed when it ran, so they
        // get a measurement of their own. Scrolling therefore measures at most once per delay, and the
        // last set of rows reported is always measured after it commits.
        if (rowsReportedWhilePendingRef.current) {
          rowsReportedWhilePendingRef.current = false;
          schedule();
        }
      }, ROWS_RENDERED_MEASUREMENT_DELAY_MS);
    }
    schedule();
  }, [measure]);

  const onRowsRendered = useCallback(
    (renderedRowIndices: Array<number>): void => {
      const key = renderedRowIndices.join(',');
      if (key === renderedRowsKeyRef.current) {
        return;
      }
      renderedRowsKeyRef.current = key;
      lastRowsRenderedAtRef.current = Date.now();
      // Virtuoso reports its rendered rows before React commits them, so they are measured after a delay.
      if (rowsRenderedMeasurementRef.current == null) {
        scheduleRowsRenderedMeasurement();
      } else {
        rowsReportedWhilePendingRef.current = true;
      }
    },
    [scheduleRowsRenderedMeasurement]
  );

  return {verticalScrollContainerRef, verticalScrollContainerIsBody, onRowsRendered};
}
